<template>
    <div>
        <h1 class="display-heading">Consultation Session</h1>
        <ul class="nav nav-tabs nav-tabs-nowrap" id="myTab" role="tablist">
            <li class="nav-item">
                <a class="nav-link active text-danger" id="consultation-details-tab" data-toggle="tab" href="#consultationDetails" role="tab">Current Consultation</a>
            </li>
            <li v-if="shouldShowCovid19Form" class="nav-item">
                <a class="nav-link" id="consultation-covid19-tab" data-toggle="tab" href="#covid19Questionnaire" role="tab">
                    <span class="text-primary" style="font-weight: bold;">
                        Covid-19 Questionnaire
                    </span>
                </a>
            </li>

            <li v-if="isConsultationInProgress" class="nav-item">
                <a class="nav-link" id="consultation-histories-tab" data-toggle="tab" href="#consultationHistories" role="tab">Previous Consultations
                    <span class="ml-1 badge" :class="{
                        'badge-primary': appointmentList.length > 0,
                        'badge-secondary':   appointmentList.length === 0,
                    }">
                        {{ appointmentList.length}}
                    </span>
                </a>
            </li>
            <li v-if="isConsultationInProgress" class="nav-item">
                <a class="nav-link" id="medical-reports-tab" data-toggle="tab" href="#medicalReports" role="tab">Medical Reports
                    <span class="ml-1 badge" :class="{
                        'badge-primary': uploadedReports.length > 0,
                        'badge-secondary': uploadedReports.length === 0,
                    }">
                        {{uploadedReports.length}}
                    </span>
                </a>
            </li>
        </ul>
        <div class="tab-content" id="myTabContent">
            <div class="tab-pane fade show active" id="consultationDetails" role="tabpanel">
                <div class="tab-content-wrapper">
                    <div class="card mb-3">
                        <div class="card-body">
                            <appointment-detail participantNameDisplay="Patient Name"
                                :isConsultation="true"
                                :consultationId="consultation.consultationId"
                                :appointmentDate="consultation.startTime"
                                :appointmentParticipant="consultation.participantName"
                                :isDoctor="true"
                                :patientGender="consultation.patientGender"
                                :patientAge="consultation.patientAge"
                                :shouldShowPatientMailingAddress="true"
                                :patientMailingAddress="consultation.patientAddress">

                                <!-- consultation.doctorName -->
                            </appointment-detail>
                        </div>
                    </div>

                    <div v-if="isConsultationInProgress" class="mb-5">
                        <h5 class="mb-3">Video Call</h5>
                        <twilio-video ref="twilioVideo" :twilioToken="this.twilioToken"
                            :twilioRoomName="this.twilioRoomName" :loading.sync="isTwilioLoading"
                            :started.sync="isTwilioStarted" :joined.sync="isTwilioJoined"
                            :statusText.sync="twilioStatus" :startText="startVideoText" :waitingText="waitingVideoText"
                            :isEnded.sync="isVideoCallEnded">
                            <p v-if="twilioStatus">{{twilioStatus}}</p>
                            <button v-show="!isTwilioStarted" class="btn btn-primary btn-primary-insv"
                                @click="startVideoCall">Start
                                Video Call Session</button>
                            <button v-show="isTwilioJoined && !isVideoCallEnded" class="btn btn-danger"
                                @click="endVideoCall">End
                                Video Call Session</button>
                        </twilio-video>

                        <div class="card mt-3">
                            <div class="card-body">
                                <p>
                                    In the event of video call cannot be conducted successfully, kindly contact the patient via the information below:
                                </p>
                                <div class="appointment-detail">
                                    <div class="appointment-detail-row">
                                        <div class="appointment-detail-title">
                                            <b>Patient Name</b>
                                        </div>
                                        <div class="appointment-detail-content">
                                            {{consultation.participantName}}
                                        </div>
                                    </div>
                                    <div class="appointment-detail-row">
                                        <div class="appointment-detail-title">
                                            <b>Contact Number</b>
                                        </div>
                                        <div class="appointment-detail-content">
                                            {{this.consultation.patientContact? this.consultation.patientContact: '-'}}
                                        </div>
                                    </div>
                                    <div class="appointment-detail-row">
                                        <div class="appointment-detail-title">
                                            <b>Email Address</b>
                                        </div>
                                        <div class="appointment-detail-content">
                                            {{this.consultation.patientEmail? this.consultation.patientEmail: '-'}}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mb-5">
                        <h5 class="mb-3">Consultation Details</h5>
                        <div class="form-group">
                            <label>Memo - For doctor use only</label>
                            <textarea v-if="isConsultationInProgress" class="form-control" rows="5"
                                v-model="form.m"></textarea>
                            <div class="card" v-else>
                                <div class="card-body" v-html="doctorMemo">
                                </div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>Doctor Comments - This will be displayed to the patient</label>
                            <textarea v-if="isConsultationInProgress" class="form-control" rows="5"
                                v-model="form.c"></textarea>
                            <div class="card" v-else>
                                <div class="card-body" v-html="doctorComments">
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="isConsultationInProgress" class="mb-5">
                        <h5 class="mb-3">ICD 10 Codes</h5>
                        <icd-codes :isEdit="true" v-model="form.icdCodes"></icd-codes>
                    </div>

                    <div v-else class="mb-5">
                        <h5 class="mb-3">ICD 10 Codes</h5>

                        <div class="card">
                            <div class="card-body">
                                <p v-if="assignedIcdCodes.length === 0" class="font-italic">
                                    No assigned ICD 10 Codes.
                                </p>

                                <p v-else v-for="(icd, index) in assignedIcdCodes" :key="index">
                                    <b>{{ icd.code }}</b> - {{ icd.description }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="mb-5">
                        <h5 class="mb-3">Doctor Special Remarks (optional)</h5>
                        <div>
                            <div class="form-group">
                                <label>Drug Allergy (optional)</label>
                                <textarea v-if="isConsultationInProgress" class="form-control" rows="5"
                                    v-model="form.dal"></textarea>
                                <div class="card" v-else>
                                    <div class="card-body" v-html="drugAllergy">
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <label>Pre-Existing Illness(es) (optional)</label>
                                <textarea v-if="isConsultationInProgress" class="form-control" rows="5"
                                    v-model="form.pai"></textarea>
                                <div class="card" v-else>
                                    <div class="card-body" v-html="preExistingIllness">
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <label>Current Medication (optional)</label>
                                <textarea v-if="isConsultationInProgress" class="form-control" rows="5"
                                    v-model="form.cm"></textarea>
                                <div class="card" v-else>
                                    <div class="card-body" v-html="currentMedication">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mb-5">
                        <h5 class="mb-3">Doctor Advice (optional)</h5>
                        <div>
                            <div class="form-group">
                                <label>Diet Advice (optional)</label>
                                <textarea v-if="isConsultationInProgress" class="form-control" rows="5"
                                    v-model="form.da"></textarea>
                                <div class="card" v-else>
                                    <div class="card-body" v-html="doctorDietAdvice">
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <label>Exercise Advice (optional)</label>
                                <textarea v-if="isConsultationInProgress" class="form-control" rows="5"
                                    v-model="form.ea"></textarea>
                                <div class="card" v-else>
                                    <div class="card-body" v-html="doctorExcerciseAdvice">
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <label>Lifestyle Advice (optional)</label>
                                <textarea v-if="isConsultationInProgress" class="form-control" rows="5"
                                    v-model="form.la"></textarea>
                                <div class="card" v-else>
                                    <div class="card-body" v-html="doctorLifestyleAdvice">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mb-5">
                        <h5 class="mb-3">Medicine Charges</h5>
                        <doctor-consultation-price ref="doctorConsultationPrice" :isConsultationInProgress="isConsultationInProgress" v-model="form.doctorConsultationPriceList" :totalPrice="consultationItemTotalPrice">

                        </doctor-consultation-price>

                        <div>
                            <a href="//www.zeptoexpress.com/booking/action/users/login/" class="btn btn-outline-primary mr-2 mb-2" target="_blank">
                                Delivery Service User Registration
                            </a>
                            <a href="//youtu.be/R3a64UouWLM" class="btn btn-outline-info mb-2" target="_blank">
                                View Delivery Video Demo
                            </a>
                        </div>
                        <small>
                            Note: This delivery service only available in Klang Valley, Penang, Johor Baharu.
                        </small>
                    </div>

                    <template v-if="isConsultationInProgress">
                        <p v-show="saveErrorMessage" class="text-danger mb-3">{{saveErrorMessage}}</p>
                        <button class="btn btn-primary btn-primary-insv mr-2 mb-2" type="button" @click="endSession">
                            Save and End Session
                        </button>
                    </template>
                    <router-link :to="{
                        'name': 'doctor-consultation-list',
                        }" class="btn btn-secondary mb-2">
                        Back to Consultation List
                    </router-link>

                </div>
            </div>
            <div v-if="shouldShowCovid19Form" class="tab-pane fade" id="covid19Questionnaire" role="tabpanel">
                <div class="tab-content-wrapper">
                    <questionnaire-covid19 ref="questionnaireCovid19" :isNew="false"
                        :consultationId="consultationId"
                        :patientName="consultation.participantName"
                        :patientIcNumber="consultation.participantIcNumber">
                    </questionnaire-covid19>
                </div>
            </div>
            <div class="tab-pane fade" id="consultationHistories" role="tabpanel">
                <div class="tab-content-wrapper">
                    <template v-if="consent.isAllowedViewConsultationHistories">
                        <h5 class="mb-3">Consultation Histories</h5>

                        <div>
                            <div class="table-responsive">
                                <table class="table table-hover">
                                    <colgroup>
                                        <col width="40">
                                        <col width="140">
                                        <col width="140">
                                        <col>
                                        <col width="250">
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th scope="col">#</th>
                                            <th scope="col">Date</th>
                                            <th scope="col">Time</th>
                                            <th scope="col">Doctor Name</th>
                                            <th scope="col">Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-show="appointmentList.length === 0">
                                            <td colspan="5" class="text-center font-italic">
                                                No Data
                                            </td>
                                        </tr>
                                        <tr v-for="(appointment, index) in appointmentList" :key="index">
                                            <th scope="row">{{index+1}}</th>
                                            <td class="text-nowrap">{{appointment.date}}</td>
                                            <td class="text-nowrap">
                                                {{appointment.time}}
                                            </td>
                                            <td>{{appointment.doctorName}}</td>
                                            <td>
                                                <router-link :to="{
                                                    'name': appointment.consultationType === 2 ? 'doctor-consultation-no-pharmacy-detail' :'doctor-consultation-detail',
                                                    'params': {
                                                        'id': appointment.consultationId
                                                        }
                                                    }"
                                                    target="_blank">
                                                    View Details
                                                </router-link>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </template>
                    <div v-else>
                        <p>Patient doesn't allow viewing of previous consultations.</p>
                    </div>
                </div>
            </div>
            <div class="tab-pane fade" id="medicalReports" role="tabpanel">
                <div class="tab-content-wrapper">
                    <template v-if="consent.isAllowedViewMedicalReports">
                        <h5 class="mb-3">Medical Reports</h5>
                        <table ref="listTable2" class="table table-hover">
                            <colgroup>
                                <col width="40">
                                <col width="200">
                                <col>
                                <col width="250">
                            </colgroup>
                            <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">Upload Date</th>
                                    <th scope="col">Report Type</th>
                                    <th scope="col">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-show="uploadedReports.length === 0">
                                    <td colspan="4" class="text-center font-italic">
                                        No Data
                                    </td>
                                </tr>
                                <tr v-for="(report, index) in uploadedReports" :key="index">
                                    <th scope="row">{{index+1}}</th>
                                    <td class="text-nowrap">{{report.date}}</td>
                                    <td class="text-nowrap">{{report.reportType}}</td>
                                    <td class="text-nowrap">
                                        <router-link :to="{'name': 'doctor-medical-report-detail' , 'params': {'id': report.reportId}}" target="_blank">View Report</router-link>
                                        <!-- <a :href="`${apiPath}MedicalReport/DownloadReportById?reportId=${report.reportId}`" target="_blank">Download Report</a> -->
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                    <div v-else>
                        <p>Patient doesn't allow viewing of the medical reports.</p>
                    </div>
                </div>
            </div>
        </div>

        <popup-modal ref="popupModalConfirmation" :hasCloseButton="false" title="End Session">
            <template slot="content">
                <p>{{confirmationMessage}}</p>
            </template>
            <template slot="footer">
                <button type="button" class="btn btn-primary btn-primary-insv" @click="saveAndEndSession">Save and End
                    Session</button>
                <button type="button" class="btn btn-secondary" @click="dismissEndSession">Cancel</button>
            </template>
        </popup-modal>

        <popup-modal ref="popupSuccessfullySaved" :hasCloseButton="false" title="Consultation Ended">
            <template slot="content">
                <p>
                    Move to "Medicine Delivery" page, you will see status is "Pending Payment".
                </p>
                <p>
                    After patient paying medication costs, status will be changed to "Pending Delivery".
                </p>
                <p>
                    Click "View Details", so you can prepare and send medications to patient.
                </p>
                <p>
                    To update patient on delivered status, click "Update to Delivered" button.
                </p>
            </template>
            <template slot="footer">
                <button type="button" class="btn btn-primary btn-primary-insv" @click="goToMedicineDelivery">Move to Medicine Delivery Page</button>
            </template>
        </popup-modal>
    </div>
</template>

<script>
import moment from 'moment';
import {
    replaceLineBreakInHtml,
    convertToUserReadableDate
} from '@/utilities';
import icdCodes from '@/components/icd-codes';
import appointmentDetail from '@/components/appointment-detail';
import twilioVideo from '@/components/twilio-video';
import doctorConsultationPrice from '@/components/doctor-consultation-price';
import popupModal from '@/components/popup-modal';
import questionnaireCovid19 from '@/components/questionnaires/covid-19';
export default {
    'name': 'DoctorConsultationDetails',
    'components': {
        icdCodes,
        appointmentDetail,
        twilioVideo,
        popupModal,
        doctorConsultationPrice,
        questionnaireCovid19
    },
    'computed': {
        shouldShowCovid19Form () {
            return this.$store.state.shouldShowCovid19Form;
        },
        consultationId () {
            return this.$route.params.id;
        },
        isConsultationInProgress () {
            return this.consultationStatus === 2;
        },
        doctorMemo () {
            return this.form.m ? replaceLineBreakInHtml(this.form.m) : 'NIL';
        },
        doctorComments () {
            return this.form.c ? replaceLineBreakInHtml(this.form.c) : 'NIL';
        },
        doctorDietAdvice () {
            return this.form.da ? replaceLineBreakInHtml(this.form.da) : 'NIL';
        },
        doctorExcerciseAdvice () {
            return this.form.ea ? replaceLineBreakInHtml(this.form.ea) : 'NIL';
        },
        doctorLifestyleAdvice () {
            return this.form.la ? replaceLineBreakInHtml(this.form.la) : 'NIL';
        },
        drugAllergy () {
            return this.form.dal ? replaceLineBreakInHtml(this.form.dal) : 'NIL';
        },
        preExistingIllness () {
            return this.form.pai ? replaceLineBreakInHtml(this.form.pai) : 'NIL';
        },
        currentMedication () {
            return this.form.cm ? replaceLineBreakInHtml(this.form.cm) : 'NIL';
        }
    },
    data () {
        return {
            convertToUserReadableDate,
            replaceLineBreakInHtml,
            'form': {
                'da': null,
                'ea': null,
                'la': null,
                'm': null,
                'ai': null,
                'c': null,
                'dal': null,
                'pai': null,
                'cm': null,
                'icdCodes': [],
                'pharmacyId': null,
                'doctorConsultationPriceList': []
            },
            'consultationItemTotalPrice': 0,
            'consultation': {
                'startTime': null,
                'consultationId': null,
                'participantName': null,
                'participantIcNumber': null,
                'doctorName': null,
                'patientGender': '',
                'patientAge': '-1',
                'patientContact': '',
                'patientEmail': '',
                'patientAddress': ''
            },
            'consultationStatus': 0,
            'twilioToken': null,
            'twilioRoomName': null,
            'isTwilioLoading': true,
            'isTwilioStarted': false,
            'isTwilioJoined': false,
            'twilioStatus': null,
            'startVideoText': 'Please click "Start Video Call Session" to make video call.',
            'waitingVideoText': 'Waiting for patient to connect...',
            'isVideoCallEnded': false,
            'confirmationMessage': null,
            'saveErrorMessage': null,
            'consent': {
                'userId': '',
                'sessionId': '',
                'isAllowedViewConsultationHistories': false,
                'isAllowedViewMedicalReports': false
            },
            'appointmentList': [],
            'uploadedReports': [],
            'assignedIcdCodes': []
        };
    },
    beforeRouteLeave (to, from, next) {
        if (this.isConsultationInProgress && this.isTwilioStarted && !this.isVideoCallEnded) {
            const answer = window.confirm('Do you really want to leave this page? Your video call session will be disconnected.');
            if (answer) {
                this.endVideoCall().then(() => {
                });
                next();
            } else {
                next(false);
            }
        } else {
            next();
        }
    },
    mounted () {
        let id = this.$route.params.id;

        if (id) {
            this.axios.get('Consultation/GetConsultationDetails', {
                'params': {
                    'consultationId': id
                }
            }).then(({
                data
            }) => {
                let consultation = data.data;

                this.consultation.startTime = consultation.st;
                this.consultation.consultationId = consultation.cis;
                this.consultation.participantName = consultation.u ? consultation.u.fn : null;
                this.consultation.participantIcNumber = consultation.cp ? consultation.cp.icn : null;
                this.consultation.doctorName = consultation.d ? consultation.d.n : null;
                this.consultation.patientGender = consultation.u ? consultation.u.g : null;
                this.consultation.patientAge = consultation.u ? consultation.u.a : -1;
                this.consultation.patientContact = consultation.u ? consultation.u.pn : null;
                this.consultation.patientEmail = consultation.u ? consultation.u.e : null;

                if (consultation.up) {
                    let mailingAddressLines = [consultation.up.mal1];

                    if (consultation.up.mal2) {
                        mailingAddressLines.push(consultation.up.mal2);
                    }
                    mailingAddressLines.push(`${consultation.up.map}, ${consultation.up.mac}`);
                    mailingAddressLines.push(consultation.up.mas);

                    this.consultation.patientAddress = mailingAddressLines.join('<br />');
                }

                this.form.da = consultation.da;
                this.form.ea = consultation.ea;
                this.form.la = consultation.la;
                this.form.m = consultation.m;
                this.form.ai = consultation.ai;
                this.form.c = consultation.c;
                this.form.dal = consultation.dal;
                this.form.pai = consultation.pai;
                this.form.cm = consultation.cm;

                this.consultationStatus = consultation.cs;

                this.consent.userId = consultation.ui;
                this.consent.sessionId = consultation.ai;

                this.form.pharmacyId = consultation.phid;

                this.consentViewConsultationHistories(consultation.cvch);
                this.consentViewMedicalReports(consultation.cvmr);

                if (consultation.cnpd && consultation.cnpd.ci) {
                    this.form.doctorConsultationPriceList = consultation.cnpd.ci.map((item) => ({
                        'type': item.t,
                        'name': item.n,
                        'price': item.p
                    }));

                    this.consultationItemTotalPrice = consultation.cnpd.ps ? consultation.cnpd.ps.pspt : 0;
                }

                if (consultation.icds) {
                    this.assignedIcdCodes = consultation.icds.map((icd) => {
                        return {
                            'code': icd.c,
                            'description': icd.d
                        };
                    });
                }
            }).catch(() => { });
        }
    },
    'methods': {
        endSession () {
            this.$refs.doctorConsultationPrice.validateForm().then((valid) => {
                if (valid) {
                    this.saveErrorMessage = '';
                    this.confirmationMessage = 'Are you sure you want to end this session?';
                    this.$refs.popupModalConfirmation.show();
                } else {
                    this.saveErrorMessage =
                            'You have error(s) in the form. Please check the input(s) before saving...';
                    this.$nextTick(() => {
                        let errorInput = document.querySelector('.is-invalid');

                        if (errorInput) {
                            errorInput.focus();
                        }
                    });
                }
            });
        },
        dismissEndSession () {
            this.$refs.popupModalConfirmation.hide();
        },
        saveAndEndSession () {
            this.confirmationMessage = 'Saving consultation data...';

            let consultationItemList = this.form.doctorConsultationPriceList.map((item) => ({
                't': item.type,
                'n': item.name,
                'p': item.price
            }));

            this.endVideoCall().then(() => {
                this.axios.post('Consultation/EndSessionConsultationNoPharmacy', {
                    'ci': this.consultationId,
                    'da': this.form.da,
                    'ea': this.form.ea,
                    'la': this.form.la,
                    'pi': this.form.pharmacyId,
                    'm': this.form.m,
                    'c': this.form.c,
                    'dal': this.form.dal,
                    'pai': this.form.pai,
                    'cm': this.form.cm,
                    'icd': this.form.icdCodes,
                    'il': consultationItemList
                }).then(({
                    data
                }) => {
                    this.$refs.popupModalConfirmation.hide().then(() => {
                        this.$refs.popupSuccessfullySaved.show();
                    });
                }).catch((error) => {
                    if (error && error.response) {
                        if (error.response.data) {
                            this.confirmationMessage = error.response.data.message;
                        } else {
                            this.confirmationMessage =
                                    'Error saving consultation, please try again.';
                        }
                    } else {
                        this.confirmationMessage =
                                    'Error saving consultation, please try again.';
                    }
                });
            });
        },
        startVideoCall () {
            this.twilioStatus = 'Connecting to video call...';
            this.isTwilioStarted = true;

            if (this.consultationId != null) {
                this.axios.get('Consultation/GetTwilioRoom', {
                    'params': {
                        'consultationId': this.consultationId
                    }
                }).then(({
                    data
                }) => {
                    this.twilioToken = data.data.t;
                    this.twilioRoomName = data.data.n;

                    this.$nextTick(() => {
                        this.$refs.twilioVideo.startTwilio();
                    });
                })
                    .catch(() => {
                        this.isTwilioStarted = false;
                    });
            }
        },
        endVideoCall () {
            if (this.isTwilioJoined) {
                return this.axios.get('Consultation/EndTwilioSession', {
                    'params': {
                        'consultationId': this.consultationId
                    }
                }).finally(() => {
                    this.isTwilioJoined = false;
                    return new Promise((resolve, reject) => {
                        resolve();
                    });
                });
            } else {
                return new Promise((resolve, reject) => {
                    resolve();
                });
            }
        },
        consentViewConsultationHistories (isConscentGiven) {
            this.consent.isAllowedViewConsultationHistories = isConscentGiven;

            if (isConscentGiven) {
                this.axios.post('ConsentService/GetConsentConsultationHistories', {
                    'gi': this.consent.userId,
                    'si': this.consent.sessionId
                }).then(({ data }) => {
                    let pagedResult = data.data;
                    let list = pagedResult.l;

                    this.appointmentList = list.map((appointment) => ({
                    // 'fullName': user.f ? user.f : '-',
                    // 'email': user.e ? user.e : '-',
                    // 'phoneNumber': user.p ? user.p : '-',
                    // 'role': user.r === 1 ? 'User' : user.r === 99 ? 'Admin' : 'User'
                        'id': appointment.ai,
                        'date': appointment.ss ? moment(appointment.ss).format('DD/MM/YYYY') : null,
                        'time': appointment.ss ? moment(appointment.ss).format('hh:mm A') : null,
                        'doctorName': appointment.tp ? appointment.tp.fn : '-',
                        'statusId': appointment.si,
                        'status': appointment.s,
                        'consultationId': appointment.sid,
                        'consultationStatusId': appointment.dc ? appointment.dc.cs : 0,
                        'consultationType': appointment.dc ? appointment.dc.ct : 0
                    }));
                });
            }
        },
        consentViewMedicalReports (isConscentGiven) {
            this.consent.isAllowedViewMedicalReports = isConscentGiven;

            if (isConscentGiven) {
                this.axios.post('ConsentService/GetConsentMedicalReports', {
                    'gi': this.consent.userId,
                    'si': this.consent.sessionId
                }).then(({ data }) => {
                    this.uploadedReports = data.data.map((report) => ({
                        'date': convertToUserReadableDate(report.ud),
                        'reportType': report.rt,
                        'reportId': report.ri
                    }));
                });
            }
        },
        goToMedicineDelivery () {
            this.$refs.popupSuccessfullySaved.hide().then(() => {
                this.$router.push({
                    'name': 'doctor-consultation-medicine-delivery-list'
                });
            });
        }
    }
};

</script>

<style>

</style>
